import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as s from './styled-connection-status';

// Limites para classificar a "saude" da conexao:
// - Online + ultima atualizacao < STALE_THRESHOLD: verde
// - Online + ultima atualizacao > STALE_THRESHOLD: amarelo (stale)
// - Offline: vermelho pulsante
const STALE_THRESHOLD_MS = 90 * 1000; // 90s sem atualizacao = atencao

// Procura no Redux a timestamp mais recente que conhecemos. Olha em
// varios slices porque dados em tempo real chegam em locais diferentes
// dependendo da tela (operacional/aplicacao tem values.ts, dashboard tem
// pollings, etc.).
const pickLatestTimestamp = (state) => {
  const candidates = [];

  // state.aplicacao.realtimeData (operacional/usina)
  const realtime = state.aplicacao && state.aplicacao.realtimeData;
  if (realtime) {
    Object.values(realtime).forEach((entry) => {
      if (entry && entry.ts) candidates.push(Number(entry.ts));
    });
  }

  // state.iot[thingName].values.ts (todas-variaveis / operacional)
  if (state.iot) {
    Object.values(state.iot).forEach((thing) => {
      if (thing && thing.values && thing.values.ts) {
        candidates.push(Number(thing.values.ts));
      }
    });
  }

  // state.aplicacao.velocimetro (objeto indexado por thingId)
  const velocimetro = state.aplicacao && state.aplicacao.velocimetro;
  if (velocimetro) {
    Object.values(velocimetro).forEach((entry) => {
      if (entry && entry.ts) candidates.push(Number(entry.ts));
    });
  }

  if (!candidates.length) return null;
  // ts pode estar em segundos (epoch) ou milisegundos — normalizamos.
  const maxRaw = Math.max(...candidates);
  return maxRaw < 1e12 ? maxRaw * 1000 : maxRaw;
};

const formatAge = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `há ${sec}s`;
  if (sec < 3600) return `há ${Math.floor(sec / 60)}min`;
  if (sec < 86400) return `há ${Math.floor(sec / 3600)}h`;
  return `há ${Math.floor(sec / 86400)}d`;
};

// variant: 'floating' (padrao, chip flutuante top-right) ou 'sidebar'
// (versao discreta para o rodape do side-menu).
// open (so usado em variant='sidebar'): se o sidebar esta aberto, mostra
// label completa; se colapsado, mostra so o dot.
const ConnectionStatus = ({ variant = 'floating', open = true }) => {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  // Tick a cada 10s para atualizar o "há Xs" sem re-render constante.
  const [, setTick] = useState(0);
  const latestTs = useSelector(pickLatestTimestamp);
  const eventosFetchError = useSelector(
    (state) => state.eventos && state.eventos.fetchError
  );

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  // Computa status (online/offline/stale) + texto + tooltip uma vez,
  // depois renderiza no container apropriado (Chip flutuante ou
  // SidebarChip discreto).
  let status;
  let label;
  let title;

  if (!online) {
    status = 'offline';
    label = 'Offline';
    title = 'Conexão de rede perdida';
  } else if (!latestTs) {
    status = 'online';
    label = 'Online';
    title = 'Aguardando dados';
  } else {
    const age = Date.now() - latestTs;
    const isStale = age > STALE_THRESHOLD_MS;
    status = isStale ? 'stale' : 'online';
    label = `há ${formatAge(age).replace('há ', '')}`;
    title = isStale
      ? `Sem dados novos há ${Math.floor(age / 1000)}s. Verifique a conexão com o servidor.`
      : 'Dados em tempo real ativos';
  }

  if (eventosFetchError) {
    title = eventosFetchError;
  }

  if (variant === 'sidebar') {
    return (
      <s.SidebarChip role="status" title={title} open={open}>
        <s.Dot status={status} />
        {open && <s.SidebarLabel>{label}</s.SidebarLabel>}
      </s.SidebarChip>
    );
  }

  // Variante flutuante (mantida para retrocompatibilidade — pode ser
  // removida do App.js depois que o sidebar adotar essa info).
  if (!online) {
    return (
      <s.Chip role="status" title={title}>
        <s.Dot status="offline" />
        Offline
      </s.Chip>
    );
  }
  if (!latestTs) {
    return (
      <s.Chip role="status" title={title}>
        <s.Dot status="online" />
        Online
      </s.Chip>
    );
  }
  const age = Date.now() - latestTs;
  return (
    <s.Chip role="status" title={title}>
      <s.Dot status={status} />
      <span>atualizado</span>
      <s.Strong>{formatAge(age)}</s.Strong>
    </s.Chip>
  );
};

export default ConnectionStatus;
